import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ShieldCheck,
  GraduationCap,
  Globe,
  Moon,
  ChevronDown,
  LogOut,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { APP_INFO } from '../../config/constants';

const sidebarNav = [
  { label: 'Overview', path: '/admin', icon: LayoutDashboard, end: true },
  { label: 'Users', path: '/admin/users', icon: Users },
  { label: 'Donations', path: '/admin/donations', icon: CreditCard },
  { label: 'Ambassadors', path: '/admin/ambassadors', icon: ShieldCheck },
  { label: 'Alumni Requests', path: '/admin/alumni-requests', icon: GraduationCap },
];

function UserAvatar({ name, image }: { name?: string; image?: string }) {
  const initials = (name || 'A')
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (image) {
    return <img src={image} alt={name || 'Admin'} className="w-8 h-8 object-cover border border-[#30363d]" />;
  }

  return (
    <div className="w-8 h-8 bg-accent-500 flex items-center justify-center text-white text-xs font-medium shrink-0">
      {initials}
    </div>
  );
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const shortName = APP_INFO.organization.split(' ')[0];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="admin-shell min-h-screen flex flex-col bg-[#0d1117] text-[#8b949e]">
      {/* Top nav */}
      <header className="h-12 shrink-0 border-b border-[#21262d] bg-[#0d1117] flex items-center px-4 lg:px-6">
        <div className="flex items-center gap-8 flex-1 min-w-0">
          <Link to="/" className="text-white font-medium text-sm shrink-0 hover:text-primary-400 transition-colors">
            {shortName}
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/projects" className="text-sm text-[#8b949e] hover:text-white transition-colors">
              Projects
            </Link>
            <Link to="/dashboard" className="text-sm text-[#8b949e] hover:text-white transition-colors">
              Dashboard
            </Link>
            <Link to="/" className="text-sm text-[#8b949e] hover:text-white transition-colors inline-flex items-center gap-1">
              Site
              <ExternalLink className="w-3 h-3" />
            </Link>
            <span className="text-sm text-white font-medium relative pb-3 pt-3 border-b-2 border-primary-500">
              Admin
            </span>
          </nav>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button type="button" aria-label="Dark mode" className="p-2 text-[#8b949e] hover:text-white transition-colors hidden sm:block">
            <Moon className="w-4 h-4" />
          </button>
          <Link to="/" aria-label="Public site" className="p-2 text-[#8b949e] hover:text-white transition-colors hidden sm:block">
            <Globe className="w-4 h-4" />
          </Link>
          <Link
            to="/admin/donations"
            className="hidden sm:inline-flex items-center px-4 py-1.5 text-sm text-white font-normal bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 transition-all"
          >
            Export
          </Link>
          <div className="relative group">
            <button type="button" className="flex items-center gap-2 pl-1">
              <UserAvatar name={user?.name} image={user?.image} />
              <ChevronDown className="w-3.5 h-3.5 text-[#8b949e] hidden lg:block" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-[#161b22] border border-[#30363d] py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="px-4 py-2 border-b border-[#30363d]">
                <p className="text-sm text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-[#8b949e] truncate">{user?.userCode}</p>
              </div>
              <Link to="/profile" className="block px-4 py-2 text-sm text-[#8b949e] hover:text-white hover:bg-[#21262d]">
                Profile
              </Link>
              <Link to="/dashboard" className="block px-4 py-2 text-sm text-[#8b949e] hover:text-white hover:bg-[#21262d]">
                User Dashboard
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-[#21262d] flex items-center gap-2"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-56 shrink-0 flex-col border-r border-[#21262d] bg-[#161b22]">
          <div className="p-4 border-b border-[#21262d]">
            <p className="text-[10px] uppercase tracking-widest text-[#8b949e] mb-2">Workspace</p>
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-white bg-[#21262d] border border-[#30363d] hover:border-[#484f58] transition-colors"
            >
              <span className="truncate">{APP_INFO.organization.toLowerCase().replace(/\s+/g, '-')}</span>
              <ChevronDown className="w-4 h-4 shrink-0 text-[#8b949e]" />
            </button>
          </div>
          <nav className="flex-1 p-3 space-y-0.5">
            {sidebarNav.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-[#21262d] text-white border-l-2 border-primary-500 -ml-px pl-[11px]'
                      : 'text-[#8b949e] hover:text-white hover:bg-[#21262d]/60'
                  }`
                }
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="admin-theme flex-1 overflow-auto admin-main">
          {children}
        </main>
      </div>
    </div>
  );
}
